import { useCallback, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { TierProgramDetail, TierProgramRow } from "@colab/shared";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  aggregateSaveStatus,
  useAutosave,
  type SaveStatus,
} from "@/hooks/use-autosave";
import { queryKeys } from "@/lib/query-keys";
import { useUpdateTierProgram } from "./use-update-program";

// Phase 7 sub-bucket 12 — program metadata settings form.
//
// Three per-field autosave streams (name / slug / description) backed by
// useAutosave with the mutator pattern: every onFlush re-reads the cached
// TierProgramDetail and patches the program in place, so two parallel
// stream flushes don't clobber each other's optimistic updates.
//
// Make-default + Archive/Restore live on the page header (cross-tab quick
// actions), not in this form — matches the convention from sub-bucket 11's
// form-builder header (publish/unpublish/archive).

export function TierProgramSettingsForm({
  program,
  onSaveStatusChange,
}: {
  program: TierProgramRow;
  onSaveStatusChange: (status: SaveStatus) => void;
}) {
  const queryClient = useQueryClient();
  const updateProgram = useUpdateTierProgram();

  const [name, setName] = useState(program.name);
  const [slug, setSlug] = useState(program.slug);
  const [description, setDescription] = useState(program.description ?? "");

  // Re-sync from server when the underlying program changes (e.g. cross-tab
  // edit, or a Make-default flip from the header that refetches detail).
  useEffect(() => {
    setName(program.name);
  }, [program.name]);
  useEffect(() => {
    setSlug(program.slug);
  }, [program.slug]);
  useEffect(() => {
    setDescription(program.description ?? "");
  }, [program.description]);

  const programId = program.id;

  // Mutator pattern: at flush time, re-read the cached program from
  // queryKeys.tiers.program(id) and only patch the changed field. Closing
  // over `program.name` here would race against per-row optimistic writes
  // and a parallel field's autosave that flushes during this one's window.
  const flushField = useCallback(
    async (field: "name" | "slug" | "description", nextRaw: string) => {
      const trimmed = nextRaw.trim();
      if (field === "name" || field === "slug") {
        if (!trimmed) return;
      }
      const key = queryKeys.tiers.program(programId);
      const cached = queryClient.getQueryData<TierProgramDetail>(key);
      const currentValue =
        field === "description"
          ? (cached?.program.description ?? null)
          : (cached?.program[field] ?? program[field]);
      const nextValue = field === "description" ? (trimmed || null) : trimmed;
      if (nextValue === currentValue) return;
      const patch =
        field === "description"
          ? { description: nextValue as string | null }
          : field === "name"
            ? { name: nextValue as string }
            : { slug: nextValue as string };
      await updateProgram.mutateAsync({ id: programId, patch });
    },
    [programId, program, queryClient, updateProgram],
  );

  const nameAutosave = useAutosave<string>({
    onFlush: (v) => flushField("name", v),
    wait: 500,
  });
  const slugAutosave = useAutosave<string>({
    onFlush: (v) => flushField("slug", v),
    wait: 500,
  });
  const descriptionAutosave = useAutosave<string>({
    onFlush: (v) => flushField("description", v),
    wait: 500,
  });

  const aggregateStatus = useMemo(
    () =>
      aggregateSaveStatus(
        nameAutosave.status,
        slugAutosave.status,
        descriptionAutosave.status,
      ),
    [nameAutosave.status, slugAutosave.status, descriptionAutosave.status],
  );

  useEffect(() => {
    onSaveStatusChange(aggregateStatus);
  }, [aggregateStatus, onSaveStatusChange]);

  return (
    <div className="max-w-2xl space-y-6 rounded-xl border bg-card p-6">
      <div className="space-y-1.5">
        <Label htmlFor="settings-name">Name</Label>
        <Input
          id="settings-name"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            nameAutosave.queue(e.target.value);
          }}
          onBlur={() => nameAutosave.flush()}
          maxLength={80}
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="settings-slug">Slug</Label>
        <Input
          id="settings-slug"
          value={slug}
          onChange={(e) => {
            setSlug(e.target.value);
            slugAutosave.queue(e.target.value);
          }}
          onBlur={() => slugAutosave.flush()}
          maxLength={64}
          pattern="^[a-z][a-z0-9-]{1,63}$"
        />
        <p className="text-xs text-muted-foreground">
          Lowercase letters, digits and hyphens. Used in URLs and HubSpot
          property values. Renaming is supported but invalidates any external
          link that hard-codes the old slug.
        </p>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="settings-description">Description</Label>
        <Textarea
          id="settings-description"
          value={description}
          onChange={(e) => {
            setDescription(e.target.value);
            descriptionAutosave.queue(e.target.value);
          }}
          onBlur={() => descriptionAutosave.flush()}
          rows={3}
          maxLength={500}
          placeholder="A short note shown above the matrix"
        />
      </div>

      <p className="text-xs text-muted-foreground">
        Toggle <span className="font-medium">Make default</span> or{" "}
        <span className="font-medium">Archive / Restore</span> from the
        program header above. Tier badges and the matrix are edited from the
        Matrix tab.
      </p>
    </div>
  );
}
